<template>
  <div class="page-container">
    <!-- 头部:当前模板徽标 + 切换模板 -->
    <div class="header-container">
      <div class="left-container">
        <span class="page-title">{{ $str.getKey('收益管理/抄表结算') }}</span>
        <!-- 结算口径切换：仅在电站开启现货收益结算（deviceFlag bit5）时出现。
             undefined 表示尚未确定，不渲染以避免闪烁 -->
        <el-radio-group v-model="settleMode" class="settle-mode-group" v-if="spotEnabled === true">
          <el-radio-button label="meter">抄表结算</el-radio-button>
          <el-radio-button label="spot">现货结算</el-radio-button>
        </el-radio-group>
        <span class="current-template-chip" v-if="currentTemplate && !isSpot">
          <el-icon class="chip-icon"><Document /></el-icon>
          {{ $str.getKey(currentTemplate.nameKey) }}
        </span>
      </div>
      <div class="right-container" v-if="!isSpot">
        <el-button class="switch-template-btn" @click="openDrawer">
          <el-icon style="margin-right: 6px"><Switch /></el-icon>
          {{ $str.getKey('收益管理/切换模板') }}
        </el-button>
      </div>
    </div>

    <!-- 主体:当前模板内容(子模板自带日期/打印/导出工具栏) -->
    <div class="main-container">
      <!-- 现货口径：独立结算单，不走模板机制 -->
      <SpotStatement v-if="isSpot && stationId" />
      <component
        v-else-if="currentTemplate && stationId"
        :is="currentTemplate.component"
        :key="isType"
        :stationId="stationId"
        :remark="activeForm.remark"
        v-bind="currentTemplate.props"
      />
      <el-empty v-else :description="$str.getKey('收益管理/暂无数据')" />
    </div>

    <!-- 切换模板抽屉(全局 RightSheet,teleport 到 body) -->
    <RightSheet v-model="drawerVisible" style="width: 760px;" class="template-drawer">
      <div class="drawer-inner">
        <!-- 标题栏 -->
        <div class="drawer-head">
          <span class="drawer-head-title">{{ $str.getKey('收益管理/切换结算模板') }}</span>
          <v-icon class="drawer-head-close" @click="drawerVisible = false">
            <X />
          </v-icon>
        </div>

        <div class="drawer-body">
        <!-- 模板卡片列表(单选) -->
        <div class="template-list">
          <div
            v-for="tpl in visibleTemplates"
            :key="tpl.id"
            :class="['template-card', draftType === tpl.id ? 'active' : '']"
            @click="selectDraft(tpl.id)"
          >
            <div class="card-radio">
              <el-radio :model-value="draftType" :value="tpl.id" @click.stop="selectDraft(tpl.id)">
                {{ $str.getKey(tpl.nameKey) }}
              </el-radio>
            </div>
            <img v-viewer class="card-img" :src="tpl.img" @click.stop />
            <div class="mark-text">{{ $str.getKey('收益管理/点击图片可放大预览') }}</div>
          </div>
        </div>

        <!-- 选中模板的参数表单 -->
        <div class="template-form-wrapper" v-if="draftTemplate">
          <div class="form-title">{{ $str.getKey('收益管理/模板参数') }}</div>
          <el-form label-position="top" class="template-form">
            <!-- single:仅用户方 -->
            <template v-if="draftTemplate.formType === 'single'">
              <el-form-item :label="$str.getKey('收益管理/用户方')" required>
                <el-input v-model="draftForm.fullName" :placeholder="$str.getKey('收益管理/请输入用户方')" :maxlength="32" show-word-limit />
              </el-form-item>
            </template>

            <!-- ratio / party:甲乙方 -->
            <template v-else>
              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item :label="$str.getKey('收益管理/用户方(甲方)')" required>
                    <el-input v-model="draftForm.fullName1" :placeholder="$str.getKey('收益管理/请输入用户方')" :maxlength="32" show-word-limit />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item :label="$str.getKey('收益管理/投资方(乙方)')" required>
                    <el-input v-model="draftForm.fullName2" :placeholder="$str.getKey('收益管理/请输入投资方')" :maxlength="32" show-word-limit />
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- ratio 独有:分成比例 + 蓄电功率/储能差值 -->
              <template v-if="draftTemplate.formType === 'ratio'">
                <el-row :gutter="16">
                  <el-col :span="12">
                    <el-form-item :label="$str.getKey('收益管理/甲方分成比例')">
                      <div class="ratio-input-wrapper">
                        <el-input-number v-model="draftForm.Bratio1" :min="0" :max="100" :precision="2" controls-position="right"
                                         @change="v => handleRatioChange(1, v)" />
                        <span class="ratio-unit">(%)</span>
                      </div>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item :label="$str.getKey('收益管理/乙方分成比例')">
                      <div class="ratio-input-wrapper">
                        <el-input-number v-model="draftForm.Bratio2" :min="0" :max="100" :precision="2" controls-position="right"
                                         @change="v => handleRatioChange(2, v)" />
                        <span class="ratio-unit">(%)</span>
                      </div>
                    </el-form-item>
                  </el-col>
                </el-row>
                <div class="mark-text left">{{ $str.getKey('收益管理/两者相加之和必须等于100') }}</div>

                <el-row :gutter="16">
                  <el-col :span="12">
                    <el-form-item :label="$str.getKey('收益管理/蓄电功率')">
                      <div class="ratio-input-wrapper">
                        <el-input-number v-model="draftForm.chargePower" :placeholder="'0'" :max="1000000" controls-position="right" />
                        <span class="ratio-unit">(kW)</span>
                      </div>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item :label="$str.getKey('收益管理/储能差值')">
                      <div class="ratio-input-wrapper">
                        <el-input-number v-model="draftForm.savings" :placeholder="'0'" :max="100" controls-position="right" />
                      </div>
                    </el-form-item>
                  </el-col>
                </el-row>
                <div class="mark-text left">{{ $str.getKey('收益管理/因储能节省或增加基本电费节省为负值，增加为正值') }}</div>
              </template>
            </template>

            <el-form-item :label="$str.getKey('收益管理/备注信息')">
              <el-input v-model="draftForm.remark" type="textarea" :maxlength="500" :rows="6" show-word-limit />
            </el-form-item>
          </el-form>
        </div>
        </div>

        <!-- 底部操作 -->
        <div class="drawer-footer">
          <el-button @click="drawerVisible = false">{{ $str.getKey('收益管理/取消') }}</el-button>
          <el-button type="primary" :disabled="!draftTemplate || draftType === isType" @click="applyTemplate">
            {{ $str.getKey('收益管理/应用此模板') }}
          </el-button>
        </div>
      </div>
    </RightSheet>
  </div>
</template>

<script setup>
import { computed, ref, reactive, watch } from "vue";
import { Document, Switch } from "@element-plus/icons-vue";
import { X } from "lucide-vue-next";
import { ElMessage, ElMessageBox } from "element-plus";
import RightSheet from "@/components/RightSheet/index.vue";
import { getIncomeQuery, getIncomeSettlement, deleteInfo } from "../components/statement/api/index.js";
import { STATEMENT_TEMPLATES, getTemplateById, getVisibleTemplates } from "../components/statement/config/statementTemplates.js";
import useAppStore from "@/store/modules/app";
import SpotStatement from "../components/statement/SpotStatement.vue";
import { useSpotSettlement } from "../composables/useSpotSettlement.js";

const $str = window.$str;
const appStore = useAppStore();
const stationId = computed(() => appStore.stationId);

/** ====== [现货收益结算：口径切换] ============================================================ */
const spot = useSpotSettlement({ defaultGranularity: 'day' });
const spotEnabled = spot.spotEnabled;
const isSpot = spot.isSpot;
// 走 setSettleMode 而不是直接改 ref，让 composable 记录「用户已手动选择」
const settleMode = computed({
  get: () => spot.settleMode.value,
  set: (v) => spot.setSettleMode(v),
});

/** 当前生效的模板 id(对应后端 templateId) */
const isType = ref(1);
/** 后端返回的原始结算配置(setFormData 时需带上 stationId 先删除) */
const currentData = ref(null);

/** 当前电站可选模板 */
const visibleTemplates = computed(() => getVisibleTemplates(stationId.value));
/** 当前生效模板配置 */
const currentTemplate = computed(() => getTemplateById(isType.value));

/**
 * 生效模板对应的表单(主要给主区子模板传 remark)。
 * 用一个对象承载所有字段,不同模板取用各自需要的子集。
 */
const activeForm = reactive(createEmptyForm());

function createEmptyForm() {
  return {
    fullName: "",
    fullName1: "",
    fullName2: "",
    Bratio1: "",
    Bratio2: "",
    chargePower: "",
    savings: "",
    remark: ""
  };
}

/** ===== 抽屉:模板选择与参数编辑 ===== */
const drawerVisible = ref(false);
/** 抽屉内草稿选中的模板 id */
const draftType = ref(1);
/** 抽屉内草稿表单 */
const draftForm = reactive(createEmptyForm());
const draftTemplate = computed(() => getTemplateById(draftType.value));

function openDrawer() {
  // 以当前生效模板为初始草稿
  draftType.value = isType.value;
  Object.assign(draftForm, activeForm);
  drawerVisible.value = true;
}

function selectDraft(id) {
  draftType.value = id;
}

/** 甲乙分成比例互补为 100(取代原 handleChange1~5) */
function handleRatioChange(which, val) {
  const v = Number(val) || 0;
  if (which === 1) {
    draftForm.Bratio1 = v;
    draftForm.Bratio2 = 100 - v;
  } else {
    draftForm.Bratio2 = v;
    draftForm.Bratio1 = 100 - v;
  }
}

/** ===== 数据加载 ===== */
watch(() => appStore.stationId, newStationId => {
  if (newStationId) getList();
}, { immediate: true });

function getList() {
  Object.assign(activeForm, createEmptyForm());
  getIncomeQuery({ stationId: stationId.value }).then(res => {
    const data = res.data || {};
    isType.value = data.templateId;
    currentData.value = JSON.parse(JSON.stringify(data));

    // 回填 activeForm(与原逻辑一致:stationInvestor 里 >1 个为甲乙方结构)
    let params = [];
    try { params = JSON.parse(data.stationInvestor) || []; } catch (e) { params = []; }

    if (params.length > 1) {
      activeForm.fullName1 = params[0].fullName;
      activeForm.fullName2 = params[1].fullName;
      activeForm.Bratio1 = params[0].ratio * 100;
      activeForm.Bratio2 = params[1].ratio * 100;
      activeForm.remark = data.remark;
      activeForm.chargePower = data.chargePower;
      activeForm.savings = data.savings;
    } else if (params.length === 1) {
      activeForm.fullName = params[0].fullName;
      activeForm.remark = data.remark;
    }
  });
}

/** ===== 应用模板(写操作,二次确认) ===== */
function applyTemplate() {
  const tpl = draftTemplate.value;
  if (!tpl) return;

  // 必填校验:用户方/投资方
  if (tpl.formType === "single") {
    if (!draftForm.fullName || !draftForm.fullName.trim()) {
      ElMessage.warning($str.getKey("收益管理/请输入用户方"));
      return;
    }
  } else {
    if (!draftForm.fullName1 || !draftForm.fullName1.trim()) {
      ElMessage.warning($str.getKey("收益管理/请输入用户方"));
      return;
    }
    if (!draftForm.fullName2 || !draftForm.fullName2.trim()) {
      ElMessage.warning($str.getKey("收益管理/请输入投资方"));
      return;
    }
  }
  // 仅 ratio(分成)模板需要校验分成比例之和为 100
  if (tpl.formType === "ratio") {
    const sum = Number(draftForm.Bratio1) + Number(draftForm.Bratio2);
    if (sum !== 100) {
      ElMessage.warning($str.getKey("收益管理/两者相加之和必须等于100"));
      return;
    }
  }

  ElMessageBox.confirm(
    $str.getKey('收益管理/切换模板将覆盖当前结算配置，是否继续？'),
    $str.getKey('收益管理/提示'),
    {
      confirmButtonText: $str.getKey('收益管理/确定'),
      cancelButtonText: $str.getKey('收益管理/取消'),
      type: "warning"
    }
  ).then(() => {
    saveTemplate(tpl, { ...draftForm });
  }).catch(() => {});
}

/** 保存结算模板(沿用原 setFormData:先删旧配置,再存新配置) */
function saveTemplate(tpl, data) {
  if (!currentData.value) return;
  const type = tpl.id;

  deleteInfo(currentData.value.stationId).then(res => {
    if (res.code !== 200) return;

    const investor = [];
    if (tpl.formType === "single") {
      // 基础模板:单一用户方,占比 100%
      investor.push({ briefName: "", fullName: data.fullName, ratio: 1, role: 1 });
    } else if (tpl.formType === "ratio") {
      // 运行泰:甲乙方按表单填写的分成比例
      investor.push({ briefName: "", fullName: data.fullName1, ratio: (data.Bratio1 / 100).toFixed(4), role: 1 });
      investor.push({ briefName: "", fullName: data.fullName2, ratio: (data.Bratio2 / 100).toFixed(4), role: 2 });
    } else {
      // 电表维度(party):无分成比例,固定 甲方100% / 乙方0%(与原 formData4/5 默认一致,保证总和=1)
      investor.push({ briefName: "", fullName: data.fullName1, ratio: "1.0000", role: 1 });
      investor.push({ briefName: "", fullName: data.fullName2, ratio: "0.0000", role: 2 });
    }

    getIncomeSettlement({
      stationId: stationId.value,
      templateId: type,
      stationInvestor: JSON.stringify(investor),
      remark: data.remark,
      chargePower: data.chargePower,
      savings: data.savings
    }).then(r => {
      if (r.code === 200) {
        ElMessage.success(r.msg);
        drawerVisible.value = false;
        getList(); // 重新拉取,刷新主区与 activeForm
      }
    });
  });
}
</script>

<style lang="scss" scoped>
.page-container {
  min-height: 100%;
  padding: 4px;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
}

/* 头部 */
.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.03);
  padding: 12px 20px;
  box-sizing: border-box;

  .left-container {
    display: flex;
    align-items: center;
    gap: 14px;

    .page-title {
      font-family: "HarmonyOS Sans SC";
      font-size: 16px;
      font-weight: 400;
      color: #2e2e2e;
      padding-left: 12px;
      border-left: 4px solid rgba(89, 110, 255, 1);
    }

    .current-template-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 30px;
      padding: 0 14px;
      border-radius: 999px;
      background: #e2e9ff;
      color: #3a68ff;
      font-family: "HarmonyOS Sans SC";
      font-size: 13px;

      .chip-icon { font-size: 15px; }
    }

    // 结算口径切换：与收益总览 Tab 的分段控件保持同一激活色
    .settle-mode-group {
      flex-shrink: 0;

      :deep(.el-radio-button__inner) {
        font-family: "HarmonyOS Sans SC";
        font-size: 14px;
      }

      :deep(.el-radio-button.is-active .el-radio-button__inner) {
        background-color: #3a68ff;
        border-color: #3a68ff;
        color: #ffffff;
      }
    }
  }

  .switch-template-btn {
    height: 40px;
    border-radius: 12px;
    background: #3a68ff;
    border-color: #3a68ff;
    color: #fff;
    font-family: "HarmonyOS Sans SC";
    font-size: 14px;

    &:hover { background: #5578ff; border-color: #5578ff; }
    .el-icon { color: #fff; }
  }
}

/* 主体 */
.main-container {
  flex: 1 0 auto;
  min-height: 0;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.03);
  padding: 16px;
  box-sizing: border-box;
}

/* 抽屉(全局 RightSheet 内部布局) */
.drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #ffffff;
  box-sizing: border-box;
}

.drawer-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 24px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);

  .drawer-head-title {
    font-family: "HarmonyOS Sans SC";
    font-size: 16px;
    font-weight: 500;
    color: #2e2e2e;
  }
  .drawer-head-close {
    cursor: pointer;
    color: #909399;
    &:hover { color: #3a68ff; }
  }
}

.drawer-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.template-list {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .template-card {
    display: grid;
    grid-template-columns: 1fr 180px;
    grid-template-areas: "radio img" "mark img";
    align-items: center;
    gap: 6px 16px;
    padding: 16px;
    border-radius: 16px;
    border: 1px solid rgba(15, 23, 42, 0.06);
    background: #fafbff;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover { border-color: rgba(89, 110, 255, 0.5); }
    &.active {
      border-color: #3a68ff;
      box-shadow: 0 2px 12px rgba(89, 110, 255, 0.18);
      background: #f5f8ff;
    }

    .card-radio { grid-area: radio; }
    .card-img {
      grid-area: img;
      width: 180px;
      height: 120px;
      object-fit: cover;
      border-radius: 12px;
      background: #f9faff;
      cursor: zoom-in;
    }
    .mark-text { grid-area: mark; }
  }
}

.template-form-wrapper {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  padding-top: 16px;

  .form-title {
    font-family: "HarmonyOS Sans SC";
    font-size: 14px;
    font-weight: 500;
    color: #2e2e2e;
    margin-bottom: 12px;
    padding-left: 10px;
    border-left: 3px solid rgba(89, 110, 255, 1);
  }
}

.ratio-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  :deep(.el-input-number) { flex: 1 1 auto; }
  .ratio-unit {
    color: rgba(46, 46, 46, 0.6);
    font-family: "HarmonyOS Sans SC";
    font-size: 14px;
    flex-shrink: 0;
  }
}

.mark-text {
  color: rgba(46, 46, 46, 0.6);
  font-size: 12px;
  font-family: "HarmonyOS Sans SC";
  &.left { text-align: left; margin: -6px 0 8px; }
}

.drawer-footer {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);

  .el-button--primary {
    background: #3a68ff;
    border-color: #3a68ff;
    color: #fff;
    &:hover { background: #5578ff; border-color: #5578ff; }
    span { color: #fff; }
  }
}

:deep(.el-input__wrapper),
:deep(.el-textarea__inner) {
  background-color: #ffffff;
  box-shadow: inset 0 0 0 1px #d4d9e5;
  border-radius: 12px;
}
:deep(.el-input.is-focus .el-input__wrapper),
:deep(.el-textarea__inner:focus) {
  box-shadow: inset 0 0 0 1px rgba(89, 110, 255, 0.5);
}
</style>
